// B2B Billing Page
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useB2B } from '../context/B2BContext';

export default function B2BBilling() {
  const { agency, token } = useB2B();
  const [searchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null); // Which plan is being upgraded to

  // Check for success/cancel from Stripe redirect
  const success = searchParams.get('success');
  const canceled = searchParams.get('canceled');
  const upgradedPlan = searchParams.get('plan');

  // Current plan (default to free)
  const currentPlan = agency?.subscription_plan || 'free';

  // Plan details
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: 'Get started with basic features',
      features: [
        '50 leads/month',
        '500 chat messages/month',
        'Basic AI assistant',
        'Email notifications',
        'Standard support'
      ],
      limits: {
        leads: 50,
        messages: 500
      }
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      description: 'Perfect for small agencies',
      features: [
        '250 leads/month',
        '2,500 chat messages/month',
        'Advanced AI assistant',
        'Custom branding',
        'Priority support',
        'Lead export (CSV)'
      ],
      limits: {
        leads: 250,
        messages: 2500
      },
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 99,
      description: 'For growing agencies',
      features: [
        '1,000 leads/month',
        '10,000 chat messages/month',
        'Premium AI with custom training',
        'White-label widget',
        'API access',
        'Dedicated support',
        'Analytics dashboard'
      ],
      limits: {
        leads: 1000,
        messages: 10000
      },
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: null,
      description: 'Custom solutions for large teams',
      features: [
        'Unlimited leads',
        'Unlimited messages',
        'Custom AI training',
        'Multiple team members',
        'SLA guarantee',
        'Dedicated account manager',
        'Custom integrations'
      ],
      limits: {
        leads: Infinity,
        messages: Infinity
      }
    }
  ];

  const currentPlanData = plans.find(p => p.id === currentPlan) || plans[0];

  // Load usage stats
  useEffect(() => {
    async function loadUsage() {
      if (!token) return;
      
      try {
        const res = await fetch('/api/b2b/billing/usage', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setUsage(data);
        }
      } catch (err) {
        console.error('Failed to load usage:', err);
      } finally {
        setLoading(false);
      }
    }
    
    loadUsage();
  }, [token]);

  // Handle upgrade click
  const handleUpgrade = async (planId) => {
    if (planId === 'enterprise') {
      // Contact sales
      window.location.href = 'mailto:sales@airgati.com?subject=Enterprise Plan Inquiry';
      return;
    }

    setUpgrading(planId);

    try {
      const res = await fetch('/api/b2b/billing/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan: planId })
      });

      const data = await res.json();

      if (res.ok && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to start checkout');
      }
    } catch (err) {
      alert('Failed to start checkout: ' + err.message);
    } finally {
      setUpgrading(null);
    }
  };

  // Handle manage subscription
  const handleManageSubscription = async () => {
    try {
      const res = await fetch('/api/b2b/billing/portal', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      alert('Failed to open billing portal: ' + err.message);
    }
  };

  // Calculate usage percentages
  const leadsPercent = usage?.usage?.leads?.percentage || 0;
  const messagesPercent = usage?.usage?.messages?.percentage || 0;

  return (
    <div>
      {/* Page Header */}
      <div className="b2b-page-header">
        <div>
          <h1 className="b2b-page-title">Billing</h1>
          <p className="b2b-page-subtitle">Manage your subscription and usage</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div style={{
          background: '#dcfce7',
          border: '1px solid #10b981',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>🎉</span>
          <div>
            <div style={{ fontWeight: 600, color: '#166534' }}>Welcome to {upgradedPlan || 'your new plan'}!</div>
            <div style={{ fontSize: '14px', color: '#15803d' }}>Your subscription is now active. Enjoy your increased limits!</div>
          </div>
        </div>
      )}

      {/* Canceled Message */}
      {canceled && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <div style={{ fontWeight: 600, color: '#92400e' }}>Checkout canceled</div>
          <div style={{ fontSize: '14px', color: '#a16207' }}>No worries! You can upgrade anytime.</div>
        </div>
      )}

      {/* Current Plan Card */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Current Plan</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>
              {currentPlanData.name}
              {currentPlanData.price !== null && (
                <span style={{ fontSize: '16px', fontWeight: 400, color: '#6b7280', marginLeft: '8px' }}>
                  ${currentPlanData.price}/month
                </span>
              )}
            </div>
          </div>
          <div style={{
            padding: '6px 12px',
            background: currentPlan === 'free' ? '#f3f4f6' : '#dcfce7',
            color: currentPlan === 'free' ? '#6b7280' : '#166534',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 500
          }}>
            {currentPlan === 'free' ? 'Free Plan' : 'Active'}
          </div>
        </div>

        {/* Usage Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          padding: '20px',
          background: '#f9fafb',
          borderRadius: '12px'
        }}>
          {/* Leads Usage */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Leads this month</span>
              <span style={{ fontSize: '14px', fontWeight: 600 }}>
                {usage?.usage?.leads?.used || 0} / {usage?.usage?.leads?.limit === Infinity ? '∞' : (usage?.usage?.leads?.limit || currentPlanData.limits.leads)}
              </span>
            </div>
            <div style={{
              height: '8px',
              background: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${leadsPercent}%`,
                background: leadsPercent > 80 ? '#ef4444' : leadsPercent > 60 ? '#f59e0b' : '#10b981',
                borderRadius: '4px',
                transition: 'width 0.3s'
              }} />
            </div>
            {usage?.limits_reached?.leads && (
              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                ⚠️ Limit reached - upgrade to capture more leads
              </div>
            )}
          </div>

          {/* Messages Usage */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Messages this month</span>
              <span style={{ fontSize: '14px', fontWeight: 600 }}>
                {usage?.usage?.messages?.used || 0} / {usage?.usage?.messages?.limit === Infinity ? '∞' : (usage?.usage?.messages?.limit || currentPlanData.limits.messages)}
              </span>
            </div>
            <div style={{
              height: '8px',
              background: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${messagesPercent}%`,
                background: messagesPercent > 80 ? '#ef4444' : messagesPercent > 60 ? '#f59e0b' : '#10b981',
                borderRadius: '4px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Days until reset */}
          <div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Resets in</div>
            <div style={{ fontSize: '24px', fontWeight: 600 }}>{usage?.days_until_reset || 30} days</div>
          </div>
        </div>

        {/* Upgrade prompt for free users */}
        {currentPlan === 'free' && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>⚡</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#92400e' }}>Upgrade to capture more leads</div>
              <div style={{ fontSize: '14px', color: '#a16207' }}>Get more leads, custom branding, and priority support</div>
            </div>
            <button 
              className="b2b-btn b2b-btn-primary"
              style={{ background: '#f59e0b', whiteSpace: 'nowrap' }}
              onClick={() => handleUpgrade('pro')}
              disabled={upgrading}
            >
              {upgrading ? 'Loading...' : 'Upgrade Now'}
            </button>
          </div>
        )}

        {/* Manage subscription for paid users */}
        {currentPlan !== 'free' && agency?.stripe_customer_id && (
          <div style={{
            marginTop: '20px',
            display: 'flex',
            gap: '12px'
          }}>
            <button 
              className="b2b-btn b2b-btn-secondary"
              onClick={handleManageSubscription}
            >
              💳 Manage Subscription
            </button>
          </div>
        )}
      </div>

      {/* Available Plans */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Available Plans</h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          {plans.map(plan => (
            <div 
              key={plan.id}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                border: plan.popular ? '2px solid #d4a853' : plan.id === currentPlan ? '2px solid #10b981' : '1px solid #e5e7eb',
                position: 'relative',
                boxShadow: plan.popular ? '0 4px 20px rgba(212, 168, 83, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #d4a853, #b8943f)',
                  color: 'white',
                  padding: '4px 16px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  MOST POPULAR
                </div>
              )}

              {/* Current plan badge */}
              {plan.id === currentPlan && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#10b981',
                  color: 'white',
                  padding: '4px 16px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  CURRENT PLAN
                </div>
              )}

              <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: plan.popular || plan.id === currentPlan ? '8px' : 0 }}>
                <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>{plan.name}</h3>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937' }}>
                  {plan.price === null ? (
                    'Custom'
                  ) : plan.price === 0 ? (
                    'Free'
                  ) : (
                    <>
                      ${plan.price}
                      <span style={{ fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>/mo</span>
                    </>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>{plan.description}</div>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                {plan.features.map((feature, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 0',
                    fontSize: '14px',
                    color: '#374151',
                    borderBottom: i < plan.features.length - 1 ? '1px solid #f3f4f6' : 'none'
                  }}>
                    <span style={{ color: '#10b981' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className="b2b-btn"
                disabled={plan.id === currentPlan || (upgrading === plan.id)}
                onClick={() => handleUpgrade(plan.id)}
                style={{
                  width: '100%',
                  background: plan.id === currentPlan 
                    ? '#f3f4f6' 
                    : plan.popular 
                      ? 'linear-gradient(135deg, #d4a853, #b8943f)'
                      : 'white',
                  color: plan.id === currentPlan ? '#9ca3af' : plan.popular ? 'white' : '#374151',
                  border: plan.popular || plan.id === currentPlan ? 'none' : '1px solid #e5e7eb',
                  cursor: plan.id === currentPlan ? 'default' : 'pointer'
                }}
              >
                {upgrading === plan.id 
                  ? 'Loading...'
                  : plan.id === currentPlan 
                    ? 'Current Plan' 
                    : plan.price === null 
                      ? 'Contact Sales'
                      : `Upgrade to ${plan.name}`
                }
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Billing History */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Billing History</h2>
        
        {currentPlan === 'free' ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📄</div>
            <div style={{ fontSize: '15px' }}>No billing history yet</div>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>
              Upgrade to a paid plan to see invoices here
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px 0', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Date</th>
                <th style={{ textAlign: 'left', padding: '12px 0', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Description</th>
                <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Amount</th>
                <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '16px 0', fontSize: '14px' }}>Jan 1, 2025</td>
                <td style={{ padding: '16px 0', fontSize: '14px' }}>{currentPlanData.name} Plan - Monthly</td>
                <td style={{ padding: '16px 0', fontSize: '14px', textAlign: 'right' }}>${currentPlanData.price}</td>
                <td style={{ padding: '16px 0', textAlign: 'right' }}>
                  <span style={{
                    padding: '4px 8px',
                    background: '#dcfce7',
                    color: '#166534',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    Paid
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Support Section */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        background: '#f9fafb',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>Need help with billing?</div>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Contact our support team for any billing questions
          </div>
        </div>
        <a 
          href="mailto:support@airgati.com"
          className="b2b-btn b2b-btn-secondary"
        >
          Contact Support
        </a>
      </div>
    </div>
  );
}
